import { z } from 'zod';
/**
 * Catálogo de permissões da aplicação: a única lista de chaves que existe.
 * O backend as exige nas rotas (`@RequirePermission`), o frontend as usa para
 * montar a navegação e o banco liga perfis a elas. Uma chave que não está
 * aqui não compila em nenhum dos dois lados.
 *
 * Nomes como os do Microsoft Graph (`User.Read`, `Mail.Send`): `Recurso.Ação`,
 * o recurso no plural e em inglês. `Read` vê, `Write` cria e altera (e exige
 * `Read`); ações próprias quando o efeito é outro (`Boxes.Discard`). A tela
 * aparece para quem tem a permissão de leitura do recurso dela.
 *
 * Título e descrição daqui são o padrão: o administrador pode reescrevê-los
 * na página Permissões, sem mexer na chave.
 */
export const PERMISSIONS = {
    'Boxes.Read': {
        title: 'Consultar caixas',
        description: 'Busca caixas, vê o histórico e baixa etiquetas.',
        // Os filtros de empresa e estabelecimento buscam no ERP.
        requires: ['Organization.Read'],
    },
    'Boxes.Write': {
        title: 'Cadastrar caixas',
        description: 'Cadastra, edita, marca como obsoleta e cancela caixas.',
        requires: ['Boxes.Read', 'Organization.Read'],
    },
    'Boxes.Discard': {
        title: 'Registrar descarte',
        description: 'Registra o descarte de caixas obsoletas, com justificativa. Não dá para desfazer.',
        requires: ['Boxes.Read', 'Organization.Read'],
    },
    'Categories.Write': {
        title: 'Manter descrições',
        description: 'Inclui, renomeia e desativa as descrições de documentos. Ver as descrições vem com Consultar caixas.',
        requires: [],
    },
    'Organization.Read': {
        title: 'Consultar empresas e estabelecimentos',
        description: 'Busca empresas e estabelecimentos no ERP.',
        requires: [],
    },
    'Users.Read': {
        title: 'Ver usuários',
        description: 'Vê quem tem acesso, com quais perfis, o histórico e se o app está no Teams de cada um.',
        // Os perfis de cada um aparecem pelo nome.
        requires: ['Roles.Read'],
    },
    'Users.Write': {
        title: 'Gerir usuários',
        description: 'Dá acesso, muda os perfis de cada um, desativa usuários e instala o app no Teams deles.',
        requires: ['Users.Read'],
    },
    'Roles.Read': {
        title: 'Ver perfis',
        description: 'Vê os perfis, o que cada um permite e o histórico deles.',
        // As permissões de cada perfil aparecem com título e descrição.
        requires: ['Permissions.Read'],
    },
    'Roles.Write': {
        title: 'Gerir perfis',
        description: 'Cria perfis, muda o nome, a descrição e as permissões de cada um, e desativa perfis.',
        requires: ['Roles.Read'],
    },
    'Permissions.Read': {
        title: 'Ver permissões',
        description: 'Vê todas as permissões do app, o que cada uma libera e exige.',
        requires: [],
    },
    'Permissions.Write': {
        title: 'Editar permissões',
        description: 'Reescreve o título e a descrição das permissões. A chave e o que ela libera vêm do código.',
        requires: ['Permissions.Read'],
    },
    'TeamsUser.Read': {
        title: 'Ver o usuário no Teams',
        description: 'Abre a página Teams > Usuário: o que o Teams e o Entra ID dizem de quem está usando.',
        requires: [],
    },
};
/** O recurso de cada permissão, como a tela o agrupa. */
export const PERMISSION_RESOURCES = {
    Boxes: 'Caixas',
    Categories: 'Descrições',
    Organization: 'Empresas e estabelecimentos',
    Users: 'Usuários',
    Roles: 'Perfis',
    Permissions: 'Permissões',
    TeamsUser: 'Teams',
};
/** `Boxes.Read` é do recurso `Boxes`. */
export const resourceOf = (permission) => permission.split('.')[0];
/**
 * Quem tem as duas conserta qualquer acesso: dá perfis e muda o que cada um
 * permite. O app sempre mantém alguém ativo com elas, e ninguém as tira de si.
 */
export const ACCESS_ADMIN_PERMISSIONS = [
    'Users.Write',
    'Roles.Write',
];
export const managesAccess = (permissions) => ACCESS_ADMIN_PERMISSIONS.every((permission) => permissions.includes(permission));
export const ALL_PERMISSIONS = Object.keys(PERMISSIONS);
export const permissionSchema = z.enum(ALL_PERMISSIONS);
export const isPermission = (value) => Object.hasOwn(PERMISSIONS, value);
/** Permissões sem as quais esta não funciona (a tela usaria uma rota negada). */
export const requiredBy = (permission) => PERMISSIONS[permission].requires;
/**
 * O que falta no conjunto: cada permissão escolhida com as que ela exige e
 * não estão nele. Vazio quando o conjunto se sustenta.
 */
export function missingRequirements(permissions) {
    const chosen = new Set(permissions);
    return permissions
        .map((permission) => ({
        permission,
        missing: requiredBy(permission).filter((needed) => !chosen.has(needed)),
    }))
        .filter(({ missing }) => missing.length > 0);
}
//# sourceMappingURL=permissions.contract.js.map